import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Inbox, Briefcase, Mail, Phone, Building, Calendar, ChevronRight, Folder } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSite, absoluteUrl } from "../context/SiteContext";
import ProjectsManager from "./admin/ProjectsManager";
import SettingsManager from "./admin/SettingsManager";
import ServicesManager from "./admin/ServicesManager";
import ClientsManager from "./admin/ClientsManager";
import TestimonialsManager from "./admin/TestimonialsManager";
import PositionsManager from "./admin/PositionsManager";
import LeadersManager from "./admin/LeadersManager";

const INQUIRY_STATUSES = ["new", "read", "responded", "archived"];
const APP_STATUSES = ["new", "shortlisted", "rejected", "hired", "archived"];

export default function AdminDashboardPage() {
  const { logout, user } = useAuth();
  const { settings, refresh: refreshSite } = useSite();
  const brand = settings.brand;
  const navigate = useNavigate();
  const [tab, setTab] = useState("inquiries");
  const [stats, setStats] = useState({ inquiries: { total: 0, new: 0 }, applications: { total: 0, new: 0 }, projects: { total: 0 } });
  const [inquiries, setInquiries] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, i, a] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/inquiries"),
        api.get("/admin/applications"),
      ]);
      setStats(s.data);
      setInquiries(i.data);
      setApps(a.data);
    } catch (e) {
      toast.error("Could not load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const updateInquiry = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      toast.success("Status updated");
      load();
      if (selectedInquiry?.id === id) setSelectedInquiry({ ...selectedInquiry, status });
    } catch { toast.error("Update failed"); }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      toast.success("Inquiry deleted");
      setSelectedInquiry(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const updateApp = async (id, status) => {
    try {
      await api.patch(`/admin/applications/${id}`, { status });
      toast.success("Status updated");
      load();
      if (selectedApp?.id === id) setSelectedApp({ ...selectedApp, status });
    } catch { toast.error("Update failed"); }
  };

  const deleteApp = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    try {
      await api.delete(`/admin/applications/${id}`);
      toast.success("Application deleted");
      setSelectedApp(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050A0F] text-[#F8FAFC]">
      {/* Header */}
      <header className="border-b border-[#1F3550] bg-[#0D1B2A] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={absoluteUrl(brand.logoUrl)} alt="" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-heading font-black tracking-wider text-sm">{brand.name}</div>
              <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#F39C12]">Control Room</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8] hidden md:inline">
              {user?.email}
            </span>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-[#1F3550] hover:border-[#F39C12] text-xs font-bold uppercase tracking-widest px-4 py-2 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <section className="border-b border-[#1F3550]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-5 gap-px bg-[#1F3550] border border-[#1F3550]">
          <StatCard label="Total Inquiries" value={stats.inquiries.total} icon={Inbox} />
          <StatCard label="New Inquiries" value={stats.inquiries.new} icon={Mail} accent />
          <StatCard label="Total Applications" value={stats.applications.total} icon={Briefcase} />
          <StatCard label="New Applications" value={stats.applications.new} icon={Briefcase} accent />
          <StatCard label="Projects" value={stats.projects?.total ?? 0} icon={Folder} />
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-[#1F3550] bg-[#0D1B2A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap overflow-x-auto">
          <TabButton testid="tab-inquiries" active={tab === "inquiries"} onClick={() => { setTab("inquiries"); setSelectedInquiry(null); }}>
            Inquiries ({inquiries.length})
          </TabButton>
          <TabButton testid="tab-applications" active={tab === "applications"} onClick={() => { setTab("applications"); setSelectedApp(null); }}>
            Applications ({apps.length})
          </TabButton>
          <TabButton testid="tab-projects" active={tab === "projects"} onClick={() => setTab("projects")}>
            Projects ({stats.projects?.total ?? 0})
          </TabButton>
          <TabButton testid="tab-services" active={tab === "services"} onClick={() => setTab("services")}>
            Services
          </TabButton>
          <TabButton testid="tab-clients" active={tab === "clients"} onClick={() => setTab("clients")}>
            Clients
          </TabButton>
          <TabButton testid="tab-testimonials" active={tab === "testimonials"} onClick={() => setTab("testimonials")}>
            Testimonials
          </TabButton>
          <TabButton testid="tab-leaders" active={tab === "leaders"} onClick={() => setTab("leaders")}>
            Leadership
          </TabButton>
          <TabButton testid="tab-positions" active={tab === "positions"} onClick={() => setTab("positions")}>
            Positions
          </TabButton>
          <TabButton testid="tab-settings" active={tab === "settings"} onClick={() => setTab("settings")}>
            Site Settings
          </TabButton>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {loading ? (
          <div className="py-20 text-center text-[#94A3B8]">Loading...</div>
        ) : tab === "projects" ? (
          <ProjectsManager onChange={refreshSite} />
        ) : tab === "services" ? (
          <ServicesManager onChange={refreshSite} />
        ) : tab === "clients" ? (
          <ClientsManager onChange={refreshSite} />
        ) : tab === "testimonials" ? (
          <TestimonialsManager onChange={refreshSite} />
        ) : tab === "leaders" ? (
          <LeadersManager onChange={refreshSite} />
        ) : tab === "positions" ? (
          <PositionsManager onChange={refreshSite} />
        ) : tab === "settings" ? (
          <SettingsManager onChange={refreshSite} />
        ) : tab === "inquiries" ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7">
              <List
                empty="No inquiries yet."
                items={inquiries}
                selectedId={selectedInquiry?.id}
                onSelect={setSelectedInquiry}
                render={(i) => (
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-heading font-bold text-[#F8FAFC] truncate">{i.name}</div>
                      <StatusBadge status={i.status} />
                    </div>
                    <div className="mt-1 text-xs text-[#94A3B8] truncate">{i.email} · {i.service || "General inquiry"}</div>
                    <div className="mt-2 text-sm text-[#94A3B8] line-clamp-2">{i.message}</div>
                    <div className="mt-3 font-mono text-[10px] tracking-[0.25em] uppercase text-[#64748B]">
                      {new Date(i.created_at).toLocaleString()}
                    </div>
                  </div>
                )}
                testidPrefix="inquiry-row"
              />
            </div>
            <div className="lg:col-span-5">
              {selectedInquiry ? (
                <DetailPanel
                  title="Inquiry Detail"
                  onDelete={() => deleteInquiry(selectedInquiry.id)}
                  testid="inquiry-detail"
                >
                  <KV label="Name" value={selectedInquiry.name} />
                  <KV icon={Mail} label="Email" value={<a href={`mailto:${selectedInquiry.email}`} className="text-[#F39C12]">{selectedInquiry.email}</a>} />
                  {selectedInquiry.phone && <KV icon={Phone} label="Phone" value={selectedInquiry.phone} />}
                  {selectedInquiry.company && <KV icon={Building} label="Company" value={selectedInquiry.company} />}
                  {selectedInquiry.service && <KV label="Service" value={selectedInquiry.service} />}
                  <KV icon={Calendar} label="Received" value={new Date(selectedInquiry.created_at).toLocaleString()} />
                  <div className="mt-4">
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">Message</div>
                    <p className="mt-2 text-[#F8FAFC] whitespace-pre-wrap leading-relaxed">{selectedInquiry.message}</p>
                  </div>
                  <div className="mt-6">
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8] mb-2">Update Status</div>
                    <div className="flex flex-wrap gap-2">
                      {INQUIRY_STATUSES.map((s) => (
                        <button
                          key={s}
                          onClick={() => updateInquiry(selectedInquiry.id, s)}
                          className={`text-[10px] uppercase tracking-widest font-bold px-3 py-2 border transition-colors ${
                            selectedInquiry.status === s
                              ? "bg-[#F39C12] text-[#050A0F] border-[#F39C12]"
                              : "border-[#1F3550] text-[#94A3B8] hover:border-[#F39C12]"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </DetailPanel>
              ) : (
                <EmptyDetail>Select an inquiry to view details.</EmptyDetail>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7">
              <List
                empty="No applications yet."
                items={apps}
                selectedId={selectedApp?.id}
                onSelect={setSelectedApp}
                render={(a) => (
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-heading font-bold text-[#F8FAFC] truncate">{a.name}</div>
                      <StatusBadge status={a.status} />
                    </div>
                    <div className="mt-1 text-xs text-[#94A3B8] truncate">{a.position} · {a.experience || "—"}</div>
                    <div className="mt-2 text-sm text-[#94A3B8] truncate">{a.email}</div>
                    <div className="mt-3 font-mono text-[10px] tracking-[0.25em] uppercase text-[#64748B]">
                      {new Date(a.created_at).toLocaleString()}
                    </div>
                  </div>
                )}
                testidPrefix="application-row"
              />
            </div>
            <div className="lg:col-span-5">
              {selectedApp ? (
                <DetailPanel
                  title="Application Detail"
                  onDelete={() => deleteApp(selectedApp.id)}
                  testid="application-detail"
                >
                  <KV label="Candidate" value={selectedApp.name} />
                  <KV label="Position" value={selectedApp.position} />
                  <KV icon={Mail} label="Email" value={<a href={`mailto:${selectedApp.email}`} className="text-[#F39C12]">{selectedApp.email}</a>} />
                  {selectedApp.phone && <KV icon={Phone} label="Phone" value={selectedApp.phone} />}
                  {selectedApp.experience && <KV label="Experience" value={selectedApp.experience} />}
                  <KV icon={Calendar} label="Applied" value={new Date(selectedApp.created_at).toLocaleString()} />
                  {selectedApp.cover_letter && (
                    <div className="mt-4">
                      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">Cover Letter</div>
                      <p className="mt-2 text-[#F8FAFC] whitespace-pre-wrap leading-relaxed">{selectedApp.cover_letter}</p>
                    </div>
                  )}
                  <div className="mt-6">
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8] mb-2">Update Status</div>
                    <div className="flex flex-wrap gap-2">
                      {APP_STATUSES.map((s) => (
                        <button
                          key={s}
                          onClick={() => updateApp(selectedApp.id, s)}
                          className={`text-[10px] uppercase tracking-widest font-bold px-3 py-2 border transition-colors ${
                            selectedApp.status === s
                              ? "bg-[#F39C12] text-[#050A0F] border-[#F39C12]"
                              : "border-[#1F3550] text-[#94A3B8] hover:border-[#F39C12]"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </DetailPanel>
              ) : (
                <EmptyDetail>Select an application to view details.</EmptyDetail>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className={`p-6 ${accent ? "bg-[#0D1B2A]" : "bg-[#050A0F]"}`}>
      <div className="flex items-start justify-between">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">{label}</div>
        <Icon className={`h-5 w-5 ${accent ? "text-[#F39C12]" : "text-[#3A506B]"}`} />
      </div>
      <div className={`mt-4 font-heading font-black text-4xl ${accent ? "text-[#F39C12]" : "text-[#F8FAFC]"}`}>{value}</div>
    </div>
  );
}

function TabButton({ children, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-4 text-xs font-bold uppercase tracking-widest border-b-2 transition-colors ${
        active ? "border-[#F39C12] text-[#F39C12]" : "border-transparent text-[#94A3B8] hover:text-[#F8FAFC]"
      }`}
    >
      {children}
    </button>
  );
}

function List({ items, render, selectedId, onSelect, empty, testidPrefix }) {
  if (!items.length) return <div className="py-20 text-center text-[#94A3B8] border border-dashed border-[#1F3550]">{empty}</div>;
  return (
    <div className="flex flex-col gap-3">
      {items.map((item) => (
        <button
          key={item.id}
          data-testid={`${testidPrefix}-${item.id}`}
          onClick={() => onSelect(item)}
          className={`text-left border bg-[#0D1B2A] p-5 transition-colors ${
            selectedId === item.id ? "border-[#F39C12]" : "border-[#1F3550] hover:border-[#3A506B]"
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">{render(item)}</div>
            <ChevronRight className="h-5 w-5 text-[#64748B] flex-shrink-0" />
          </div>
        </button>
      ))}
    </div>
  );
}

function DetailPanel({ children, title, onDelete, testid }) {
  return (
    <div data-testid={testid} className="border border-[#1F3550] bg-[#0D1B2A] p-6 lg:sticky lg:top-24">
      <div className="flex items-center justify-between mb-5">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F39C12]">{title}</div>
        <button
          onClick={onDelete}
          data-testid="detail-delete"
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#EF4444] hover:text-red-300"
        >
          <Trash2 className="h-3 w-3" /> Delete
        </button>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function KV({ label, value, icon: Icon }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && <Icon className="h-4 w-4 text-[#F39C12] mt-0.5 flex-shrink-0" />}
      <div>
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">{label}</div>
        <div className="mt-1 text-[#F8FAFC] text-sm">{value}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const color = {
    new: "bg-[#F39C12] text-[#050A0F]",
    read: "bg-[#3A506B] text-[#F8FAFC]",
    responded: "bg-[#10B981] text-[#050A0F]",
    archived: "bg-[#1F3550] text-[#94A3B8]",
    shortlisted: "bg-[#0052CC] text-[#F8FAFC]",
    rejected: "bg-[#EF4444] text-[#F8FAFC]",
    hired: "bg-[#10B981] text-[#050A0F]",
  }[status] || "bg-[#1F3550] text-[#94A3B8]";
  return (
    <span className={`font-mono text-[9px] tracking-[0.25em] uppercase px-2 py-1 ${color}`}>
      {status}
    </span>
  );
}

function EmptyDetail({ children }) {
  return (
    <div className="border border-dashed border-[#1F3550] p-12 text-center text-[#94A3B8] text-sm">
      {children}
    </div>
  );
}
